import React from 'react'
import { styled } from '@mui/material/styles'
import {
    Box,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { VideoPlayer } from './index'
import { getMediaUrl } from '@assembly/utils'
import ApplicationDetailsTab from './ApplicationDetailsTab'
import { Document, Page, pdfjs } from 'react-pdf'
import { useSearchParams } from 'react-router-dom'

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`

const TabPanel = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(2),
    },
}))

const StyledTab = styled(Tab)(({ theme }) => ({
    fontFamily: 'Space Grotesk',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    [theme.breakpoints.down('md')]: {
        fontSize: '14px',
    },
}))

const StyledTabs = styled(Tabs)({
    borderBottom: '1px solid #e8e8e8',
})

const VideoTitle = styled(Typography)(({ theme }) => ({
    fontFamily: 'Space Grotesk',
    fontWeight: 500,
    fontSize: '20px',
    [theme.breakpoints.down('md')]: {
        fontSize: '18px',
    },
}))

const VideoContainer = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}))

const MatchScoreValue = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    fontSize: '36px',
    color: theme.palette.primary.main,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    textAlign: 'center',
}))

const MatchScoreDetails = styled(Typography)(() => ({
    whiteSpace: 'pre-line',
    fontFamily: 'Roboto',
    lineHeight: '1.8',
}))

const ScoreSection = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
}))

const ScoreSectionTitle = styled(Typography)(({ theme }) => ({
    fontFamily: 'Space Grotesk',
    fontWeight: 600,
    fontSize: '20px',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
}))

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <TabPanel>{children}</TabPanel>
            )}
        </div>
    )
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    }
}

// PDF Viewer component
function PDFViewer({ pdfURL }: { pdfURL: string }) {
    const [numPages, setNumPages] = React.useState<number>(0)

    function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
        setNumPages(numPages)
    }

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '690px',
                height: '700px',
                overflowY: 'scroll',
                overflowX: 'hidden',
                marginTop: '15px',
            }}
        >
            <Document file={pdfURL} onLoadSuccess={onDocumentLoadSuccess}>
                {Array.from(new Array(numPages), (el, index) => (
                    <Page
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                        width={690}
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                    />
                ))}
            </Document>
        </Box>
    )
}

// Define a type for assessment responses
interface AssessmentResponse {
    skillsAssessmentId: string;
    jobID: string;
    title: string;
    type: string;
    videoResponse?: {
        id: string;
        name: string;
        thumbnail: string | null;
        media: string;
        sizeMB: number;
        uploadComplete: boolean;
    };
    videoTranscript: string | null;
    textResponse: string | null;
}

interface VideoAndAssessmentTabsProps {
    assessmentResponses: Record<string, AssessmentResponse>;
    applicationDetails?: {
        questionnaire: Record<string, any>
        jobTitle: string
        applyDate: number
    }
    resumeUrl?: string
    matchScore?: {
        score: number
        details: string
    }
    portfolio?: Array<{
        id: string;
        name: string;
        thumbnail: string | null;
        media: string;
        sizeMB: number;
        uploadComplete: boolean;
    }>
}

// Helper function to parse match score details into sections
const parseMatchScoreDetails = (details: string) => {
    if (!details) return [];

    // Split the text by line breaks and filter out empty lines
    const lines = details.split('\n').filter(line => line.trim() !== '');

    // Group lines into sections based on patterns
    const sections: { title: string, content: string }[] = [];
    let currentSection = { title: '', content: '' };

    lines.forEach(line => {
        // Check if line looks like a new section header (ends with a colon, points, or arrow)
        const isHeader = /:\s*(\d+|\+\d+)\s*(points|→)/.test(line);

        if (isHeader) {
            // Save the previous section if it exists
            if (currentSection.title) {
                sections.push({ ...currentSection });
            }
            currentSection = { title: line, content: '' };
        } else if (currentSection.title) {
            // Add to current section content
            currentSection.content += (currentSection.content ? '\n' : '') + line;
        } else {
            // If we don't have a title yet, this is the first section title
            currentSection.title = line;
        }
    });

    // Add the last section
    if (currentSection.title) {
        sections.push(currentSection);
    }

    return sections;
};

export default function VideoAndAssessmentTabs({
    assessmentResponses,
    applicationDetails,
    resumeUrl,
    matchScore,
    portfolio
}: VideoAndAssessmentTabsProps) {
    const [value, setValue] = React.useState(0)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [searchParams] = useSearchParams()
    const showApp = searchParams.get('showApp') === 'true'

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue)
    }

    // Convert assessmentResponses object to array for easier processing
    const assessments = Object.values(assessmentResponses) as AssessmentResponse[]

    // Group assessments by type
    const allVideoAssessments = assessments.filter(assessment => assessment.type === 'VIDEO')

    // Don't show the first video assessment in the tab if it's shown as hero
    // We check if we have more than one video assessment to avoid empty tabs
    const videoAssessments = allVideoAssessments.length > 1 ? allVideoAssessments.slice(1) : allVideoAssessments

    const textAssessments = assessments.filter(assessment => assessment.type === 'TEXT')

    // Calculate tab indices
    const videoTabIndex = 0
    const resumeTabIndex = 1
    const portfolioTabIndex = 2
    const matchScoreTabIndex = portfolio ? 3 : 2
    const applicationTabIndex = portfolio ? 4 : 3

    // Determine which tabs to show
    const showVideoTab = Object.keys(assessmentResponses).length > 0
    const showResumeTab = !!resumeUrl
    const showPortfolioTab = portfolio && portfolio.length > 0
    const showMatchScoreTab = !!matchScore
    const showApplicationTab = !!applicationDetails && showApp

    // Parse match score details if available
    const matchScoreSections = matchScore ? parseMatchScoreDetails(matchScore.details) : [];

    return (
        <Box sx={{ width: '100%' }}>
            <StyledTabs
                value={value}
                onChange={handleChange}
                variant={isMobile ? 'scrollable' : 'fullWidth'}
                scrollButtons={isMobile ? 'auto' : false}
                allowScrollButtonsMobile
            >
                {showVideoTab && <StyledTab label="Videos" />}
                {showResumeTab && <StyledTab label="Resume" />}
                {showPortfolioTab && <StyledTab label="Portfolio" />}
                {showMatchScoreTab && <StyledTab label="Match Score" />}
                {showApplicationTab && <StyledTab label="Application" />}
            </StyledTabs>

            {/* Video Assessments Tab with Collapsible Videos */}
            {showVideoTab && (
                <CustomTabPanel value={value} index={videoTabIndex}>
                    {videoAssessments.map((assessment, index) => {
                        // Calculate the video URL safely
                        let videoUrl = '';
                        if (assessment.videoResponse && assessment.videoResponse.media) {
                            const mediaUrl = getMediaUrl(assessment.videoResponse.media as string);
                            // Only use mediaUrl if it's not undefined
                            if (mediaUrl) {
                                videoUrl = mediaUrl;
                            }
                        }

                        return (
                            <VideoContainer key={index}>
                                <Accordion defaultExpanded={false}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls={`assessment-content-${index}`}
                                        id={`assessment-header-${index}`}
                                    >
                                        <VideoTitle>{assessment.title}</VideoTitle>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {assessment.videoResponse && assessment.videoResponse.media && videoUrl && (
                                            <VideoPlayer url={videoUrl} />
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </VideoContainer>
                        );
                    })}
                </CustomTabPanel>
            )}

            {/* Resume Tab */}
            {showResumeTab && (
                <CustomTabPanel value={value} index={resumeTabIndex}>
                    {resumeUrl && <PDFViewer pdfURL={resumeUrl} />}
                </CustomTabPanel>
            )}

            {/* Portfolio Tab */}
            {showPortfolioTab && (
                <CustomTabPanel value={value} index={portfolioTabIndex}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {portfolio?.map((item, index) => {
                            const mediaUrl = getMediaUrl(item.media);
                            if (!mediaUrl) return null;

                            return (
                                <Paper key={index} elevation={0} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Space Grotesk' }}>
                                        {item.name}
                                    </Typography>
                                    {item.media.toLowerCase().endsWith('.pdf') ? (
                                        <PDFViewer pdfURL={mediaUrl} />
                                    ) : (
                                        <Box
                                            component="img"
                                            src={mediaUrl}
                                            alt={item.name}
                                            sx={{
                                                width: '100%',
                                                maxHeight: '500px',
                                                objectFit: 'contain',
                                                borderRadius: '8px'
                                            }}
                                        />
                                    )}
                                </Paper>
                            );
                        })}
                    </Box>
                </CustomTabPanel>
            )}

            {/* Match Score Tab */}
            {showMatchScoreTab && matchScore && (
                <CustomTabPanel value={value} index={matchScoreTabIndex}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h5" sx={{ fontFamily: 'Space Grotesk', fontWeight: 500 }}>
                            Candidate Match Score
                        </Typography>
                        <MatchScoreValue>{matchScore.score}%</MatchScoreValue>
                    </Box>

                    {matchScoreSections.length > 0 ? (
                        matchScoreSections.map((section, index) => (
                            <ScoreSection key={index}>
                                <ScoreSectionTitle>{section.title}</ScoreSectionTitle>
                                <MatchScoreDetails>{section.content}</MatchScoreDetails>
                            </ScoreSection>
                        ))
                    ) : (
                        <MatchScoreDetails>{matchScore.details}</MatchScoreDetails>
                    )}
                </CustomTabPanel>
            )}

            {/* Application Details Tab */}
            {showApplicationTab && applicationDetails && (
                <CustomTabPanel value={value} index={applicationTabIndex}>
                    <ApplicationDetailsTab
                        questionnaire={applicationDetails.questionnaire}
                        jobTitle={applicationDetails.jobTitle}
                        applyDate={applicationDetails.applyDate}
                    />
                </CustomTabPanel>
            )}
        </Box>
    )
} 